/**
 * Color utility functions for theme and accessibility operations
 */

export interface RGB {
  r: number; // Red (0-255)
  g: number; // Green (0-255)
  b: number; // Blue (0-255)
}

export interface HSV {
  h: number; // Hue (0-360)
  s: number; // Saturation (0-1)
  v: number; // Value/Brightness (0-1)
}

/**
 * Blends two colors using alpha compositing.
 * If backgroundColor has alpha, it's first composited on white.
 * Then overlayColor is blended on top of the result.
 *
 * @param backgroundColor Background color in RGBA format (e.g., "rgba(21, 101, 192, 1)")
 * @param overlayColor Overlay color with transparency in RGBA format (e.g., "rgba(255, 255, 255, 0.114)")
 * @returns Blended color as hex string (e.g., "#1e6bc4")
 * @throws Error if color format is invalid
 */
export function blendColorsWithAlpha(
  backgroundColor: string,
  overlayColor: string,
): string {
  // Parse background color
  const bgMatch = backgroundColor.match(
    /rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]*)\)/,
  );
  if (!bgMatch) {
    throw new Error(`Invalid background color format: "${backgroundColor}"`);
  }
  const bgRGB: RGB = {
    r: parseInt(bgMatch[1]),
    g: parseInt(bgMatch[2]),
    b: parseInt(bgMatch[3]),
  };
  const bgAlpha = bgMatch[4] ? parseFloat(bgMatch[4]) : 1;

  // Parse overlay color
  const overlayMatch = overlayColor.match(
    /rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]*)\)/,
  );
  if (!overlayMatch) {
    throw new Error(`Invalid overlay color format: "${overlayColor}"`);
  }
  const overlayRGB: RGB = {
    r: parseInt(overlayMatch[1]),
    g: parseInt(overlayMatch[2]),
    b: parseInt(overlayMatch[3]),
  };
  const overlayAlpha = overlayMatch[4] ? parseFloat(overlayMatch[4]) : 1;

  // Step 1: If background has alpha, composite it on white first
  let compositedBg: RGB;
  if (bgAlpha < 1) {
    compositedBg = {
      r: 255 * (1 - bgAlpha) + bgRGB.r * bgAlpha,
      g: 255 * (1 - bgAlpha) + bgRGB.g * bgAlpha,
      b: 255 * (1 - bgAlpha) + bgRGB.b * bgAlpha,
    };
  } else {
    compositedBg = bgRGB;
  }

  // Step 2: Blend overlay color on top of composited background
  const blended: RGB = {
    r: overlayRGB.r * overlayAlpha + compositedBg.r * (1 - overlayAlpha),
    g: overlayRGB.g * overlayAlpha + compositedBg.g * (1 - overlayAlpha),
    b: overlayRGB.b * overlayAlpha + compositedBg.b * (1 - overlayAlpha),
  };

  // Convert to hex
  const toHex = (n: number) => {
    const hex = Math.round(n).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(blended.r)}${toHex(blended.g)}${toHex(blended.b)}`;
}

export interface RedDetectionResult {
  isRed: boolean;
  intensity: number;
  redComponent: number;
}

/**
 * Converts RGB values to HSV color space
 * @param r Red component (0-255)
 * @param g Green component (0-255)
 * @param b Blue component (0-255)
 * @returns HSV object with hue (0-360), saturation (0-1), and value (0-1)
 */
export function rgbToHsv(r: number, g: number, b: number): HSV {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const diff = max - min;

  let h = 0;
  const s = max === 0 ? 0 : diff / max;
  const v = max;

  if (diff !== 0) {
    switch (max) {
      case r:
        h = ((g - b) / diff + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / diff + 2) / 6;
        break;
      case b:
        h = ((r - g) / diff + 4) / 6;
        break;
    }
  }

  return { h: h * 360, s, v };
}

/**
 * Detects if a color is red using strict HSV-based criteria
 * @param color RGBA or RGB color string (e.g., "rgba(255, 0, 0, 1)" or "rgb(255, 0, 0)")
 * @returns Object with red detection results including intensity and red component
 * @throws Error if color format is invalid
 */
export function detectRedColor(color: string): RedDetectionResult {
  const rgba = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]*)\)/);

  if (!rgba) {
    throw new Error(
      `Invalid color format: "${color}". Expected RGBA or RGB format like "rgba(255, 0, 0, 1)" or "rgb(255, 0, 0)".`,
    );
  }

  const r = parseInt(rgba[1]);
  const g = parseInt(rgba[2]);
  const b = parseInt(rgba[3]);

  const { h, s, v } = rgbToHsv(r, g, b);

  // Strict red detection criteria:
  // 1. Hue must be in red range (0-20° or 345-360°)
  const isRedHue = (h >= 0 && h <= 20) || (h >= 345 && h <= 360);

  // 2. Minimum saturation of 50% for vibrant colors
  const hasMinimumSaturation = s >= 0.5;

  // 3. Minimum brightness of 30% to avoid very dark colors
  const hasMinimumValue = v >= 0.3;

  // 4. Red component must dominate over green and blue combined
  const redDominance = r > (g + b) * 1.2;

  // 5. Minimum absolute red value (47% of 255 = 120)
  const hasMinimumRed = r >= 120;

  const isRed =
    isRedHue &&
    hasMinimumSaturation &&
    hasMinimumValue &&
    redDominance &&
    hasMinimumRed;

  const intensity = isRed ? Math.min(s * v * 1.2, 1) : 0;
  const redComponent = r / 255;

  return { isRed, intensity, redComponent };
}

/**
 * Calculates the relative luminance of a color according to WCAG guidelines
 * @param color RGBA or RGB color string (e.g., "rgba(255, 0, 0, 1)" or "rgb(255, 0, 0)")
 * @returns Relative luminance value (0-1)
 * @throws Error if color format is invalid
 */
export function getRelativeLuminance(color: string): number {
  const rgba = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]*)\)/);
  if (!rgba) {
    throw new Error(
      `Invalid color format: "${color}". Expected RGBA or RGB format like "rgba(255, 0, 0, 1)" or "rgb(255, 0, 0)".`,
    );
  }

  const [r, g, b] = [
    parseInt(rgba[1]),
    parseInt(rgba[2]),
    parseInt(rgba[3]),
  ].map((c) => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Calculates the WCAG contrast ratio between two colors
 * @param color1 First color (RGBA or RGB string, e.g., "rgba(255, 0, 0, 1)" or "rgb(255, 0, 0)")
 * @param color2 Second color (RGBA or RGB string, e.g., "rgba(255, 0, 0, 1)" or "rgb(255, 0, 0)")
 * @returns Contrast ratio (1-21, where 21 is maximum contrast)
 * @throws Error if either color format is invalid
 */
export function calculateWCAGContrast(color1: string, color2: string): number {
  try {
    const lum1 = getRelativeLuminance(color1);
    const lum2 = getRelativeLuminance(color2);
    const brightest = Math.max(lum1, lum2);
    const darkest = Math.min(lum1, lum2);

    return (brightest + 0.05) / (darkest + 0.05);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    throw new Error(
      `Failed to calculate WCAG contrast between "${color1}" and "${color2}": ${errorMessage}`,
    );
  }
}

/**
 * Checks if two colors meet WCAG AA accessibility requirements
 * @param backgroundColor Background color (RGBA or RGB string, e.g., "rgba(255, 0, 0, 1)" or "rgb(255, 0, 0)")
 * @param textColor Text color (RGBA or RGB string, e.g., "rgba(255, 0, 0, 1)" or "rgb(255, 0, 0)")
 * @param level WCAG level ('AA' = 4.5:1, 'AAA' = 7:1)
 * @returns True if colors meet the specified WCAG requirements
 * @throws Error if either color format is invalid
 */
export function meetsWCAGRequirements(
  backgroundColor: string,
  textColor: string,
  level: 'AA' | 'AAA' = 'AA',
): boolean {
  const contrast = calculateWCAGContrast(backgroundColor, textColor);
  const threshold = level === 'AAA' ? 7 : 4.5;
  return contrast >= threshold;
}

/**
 * Gets the best contrast color (black or white) for a given background
 * @param backgroundColor Background color (RGBA or RGB string, e.g., "rgba(255, 0, 0, 1)" or "rgb(255, 0, 0)")
 * @param darkColor Dark text color (default: #000000)
 * @param lightColor Light text color (default: #ffffff)
 * @returns The color with better contrast against the background
 * @throws Error if background color format is invalid
 */
export function getBestContrastColor(
  backgroundColor: string,
  darkColor: string = '#000000',
  lightColor: string = '#ffffff',
): string {
  const rgbaMatch = backgroundColor.match(
    /rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]*)\)/,
  );
  if (!rgbaMatch) {
    throw new Error(
      `Invalid background color format: "${backgroundColor}". Expected RGBA or RGB format like "rgba(255, 0, 0, 1)" or "rgb(255, 0, 0)".`,
    );
  }

  const [, r, g, b] = rgbaMatch;
  const red = parseInt(r);
  const green = parseInt(g);
  const blue = parseInt(b);

  // Calculate luminance using standard formula
  const luminance = (0.299 * red + 0.587 * green + 0.114 * blue) / 255;

  return luminance > 0.6 ? darkColor : lightColor;
}

/**
 * Checks if two colors are similar within a threshold
 * @param color1 First color (RGBA or RGB string, e.g., "rgba(255, 0, 0, 1)" or "rgb(255, 0, 0)")
 * @param color2 Second color (RGBA or RGB string, e.g., "rgba(255, 0, 0, 1)" or "rgb(255, 0, 0)")
 * @param threshold Similarity threshold (0-441, default: 50)
 * @returns True if colors are similar within the threshold
 * @throws Error if either color format is invalid
 */
export function colorsAreSimilar(
  color1: string,
  color2: string,
  threshold: number = 50,
): boolean {
  const rgba1 = color1.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]*)\)/);
  const rgba2 = color2.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]*)\)/);

  if (!rgba1) {
    throw new Error(
      `Invalid color1 format: "${color1}". Expected RGBA or RGB format like "rgba(255, 0, 0, 1)" or "rgb(255, 0, 0)".`,
    );
  }

  if (!rgba2) {
    throw new Error(
      `Invalid color2 format: "${color2}". Expected RGBA or RGB format like "rgba(255, 0, 0, 1)" or "rgb(255, 0, 0)".`,
    );
  }

  const r1 = parseInt(rgba1[1]),
    g1 = parseInt(rgba1[2]),
    b1 = parseInt(rgba1[3]);
  const r2 = parseInt(rgba2[1]),
    g2 = parseInt(rgba2[2]),
    b2 = parseInt(rgba2[3]);

  // Calculate color difference using Euclidean distance
  const diff = Math.sqrt(
    Math.pow(r1 - r2, 2) + Math.pow(g1 - g2, 2) + Math.pow(b1 - b2, 2),
  );

  return diff < threshold;
}
